import ungroupedRoutesData from '../const/NavigationPaths';

import data from '../assets/apiRoutes.json';

/**
 * Finds an item in a tree structure by path
 * @param {Array} items - Tree structure items
 * @param {string} path - Path to search for
 * @param {Array} parentChain - Array to build parent chain
 * @returns {Object|null} - Found item with parent chain
 */
function findItemByPath(items, path, parentChain = []) {
  for (let item of items) {
    const currentChain = [...parentChain, item];
    
    // Check if this item matches the path
    if (item.path === path) {
      return {
        item: item,
        parentChain: parentChain
      };
    }
    
    // Recursively search in children
    if (item.items && item.items.length > 0) {
      const found = findItemByPath(item.items, path, currentChain);
      if (found) {
        return found;
      }
    }
  }
  return null;
}

/**
 * Finds a route path for a given label and module
 * @param {string} label - Label to search for
 * @param {string} moduleKey - Module key (e.g., "Warehouse Management")
 * @param {string} excludePath - Path to exclude (current path)
 * @returns {string|null} - Route path or null
 */
function findRoutePathForLabel(label, moduleKey, excludePath = null) {
  // Try exact match first
  let route = ungroupedRoutesData.find(r => 
    r.key === moduleKey && 
    r.name === label && 
    r.value !== excludePath
  );
  
  if (route) return route.value;
  
  // Try to find route where name contains the label
  route = ungroupedRoutesData.find(r => 
    r.key === moduleKey && 
    r.name.includes(label) && 
    r.value !== excludePath
  );
  
  if (route) return route.value;
  
  // For "Transactions" folder, try to find a default transaction route
  if (label === 'Transactions' && moduleKey === 'Warehouse Management') {
    route = ungroupedRoutesData.find(r => 
      r.key === moduleKey && 
      (r.name.includes('Transaction') || r.value.includes('/wm/transaction')) &&
      r.value !== excludePath
    );
    if (route) return route.value;
  }
  
  return null;
}

/**
 * Builds breadcrumb items from current route
 * @param {string} currentPath - Current route pathname
 * @returns {Array} - Array of breadcrumb items with label and path
 */
export function buildBreadcrumbFromRoute(currentPath) {
  const breadcrumbItems = [];
  
  // Special handling for schedule/unschedule appointment routes - use same breadcrumb as dock door selection
  if (currentPath.startsWith('/scheduleappointment/') || currentPath.startsWith('/unscheduleappointment/')) {
    const currentRoute = ungroupedRoutesData.find(route => route.value === '/wm/dockdoorselection');
    if (currentRoute) {
      if (currentRoute.key && currentRoute.key.trim() !== '') {
        breadcrumbItems.push({
          label: currentRoute.key,
          path: null,
          isClickable: false
        });
      }
      const routeName = currentRoute.name;
      const parts = routeName.split(/->|>/).map(part => part.trim());
      if (parts.length === 1) {
        breadcrumbItems.push({
          label: routeName,
          path: currentPath,
          isClickable: false
        });
      } else {
        parts.forEach((part, index) => {
          const isLast = index === parts.length - 1;
          breadcrumbItems.push({
            label: part,
            path: isLast ? currentPath : null,
            isClickable: false
          });
        });
      }
      return breadcrumbItems;
    }
  }
  
  // Special handling for Item Management route - its URL carries the permission segment
  // (/im/itemManagement/ItemManagement), so match by prefix and build from the RoutesList entry
  // (Inventory Management / Transactions / Item Management) instead of the raw path segments.
  if (currentPath.startsWith('/im/itemManagement')) {
    const currentRoute = ungroupedRoutesData.find(route => route.value === '/im/itemManagement/ItemManagement');
    if (currentRoute) {
      if (currentRoute.key && currentRoute.key.trim() !== '') {
        breadcrumbItems.push({ label: currentRoute.key, path: null, isClickable: false });
      }
      const parts = currentRoute.name.split(/->|>/).map(part => part.trim());
      parts.forEach((part, index) => {
        breadcrumbItems.push({
          label: part,
          path: index === parts.length - 1 ? currentPath : null,
          isClickable: false
        });
      });
      return breadcrumbItems;
    }
  }

  // Special handling for InventoryOperation route - use same breadcrumb as Transaction Entry Form
  if (currentPath.startsWith('/im/InventoryOperation')) {
    const currentRoute = ungroupedRoutesData.find(route => route.value === '/im/TransactionEntryForm/TransactionEntryForm');
    if (currentRoute) {
      if (currentRoute.key && currentRoute.key.trim() !== '') {
        breadcrumbItems.push({
          label: currentRoute.key,
          path: null,
          isClickable: false
        });
      }
      const routeName = currentRoute.name;
      const parts = routeName.split(/->|>/).map(part => part.trim());
      if (parts.length === 1) {
        breadcrumbItems.push({
          label: routeName,
          path: currentPath,
          isClickable: false
        });
      } else {
        parts.forEach((part, index) => {
          const isLast = index === parts.length - 1;
          breadcrumbItems.push({
            label: part,
            path: isLast ? currentPath : null,
            isClickable: false
          });
        });
      }
      return breadcrumbItems;
    }
  }
  
  // Find the route in RoutesList (exact match first)
  let currentRoute = ungroupedRoutesData.find(route => route.value === currentPath);
  
  // If no exact match, try to find routes that match the base path (for routes with query parameters)
  if (!currentRoute) {
    // Remove query parameters and hash for matching
    const basePath = currentPath.split('?')[0].split('#')[0];
    currentRoute = ungroupedRoutesData.find(route => {
      const routeBasePath = route.value.split('?')[0].split('#')[0];
      return routeBasePath === basePath;
    });
  }
  
  if (currentRoute) {
    // Add the main module (key) as first breadcrumb only if key is not empty
    if (currentRoute.key && currentRoute.key.trim() !== '') {
      breadcrumbItems.push({
        label: currentRoute.key,
        path: null,
        isClickable: false
      });
    }

    // Parse the route name to extract hierarchy
    // Format: "Transactions -> Transaction Management" or just "Transaction Management"
    const routeName = currentRoute.name;
    
    // Check for "->" or ">" separator
    const parts = routeName.split(/->|>/).map(part => part.trim());
    
    // If no separator found, use the route name as a single item
    if (parts.length === 1) {
      breadcrumbItems.push({
        label: routeName,
        path: currentPath,
        isClickable: false
      });
    } else {
      // Add each part as a breadcrumb item
      parts.forEach((part, index) => {
        const isLast = index === parts.length - 1;
        breadcrumbItems.push({
          label: part,
          path: isLast ? currentPath : null,
          isClickable: false
        });
      });
    }
  } else {
    
    // If route not found in RoutesList, try to parse from path
    const pathParts = currentPath.split('/').filter(part => part);
    
    if (pathParts.length > 0) {
      // Map path segments to module names
      const moduleMap = {
        'wm': 'Warehouse Management',
        'om': 'Order Management',
        'tm': 'Orchard Management',
        'so': 'Sow Order Database',
        'at': 'Advanced Reporting Tool',
        'dd': 'Dock Door Scheduler',
        'db': 'Dashboard',
        'dbl': 'Dashboard Lite',
        'im': 'Inventory Management',
        'id': 'ID Master Data Entry',
        'rf': 'Radio Frequency',
        'ta': 'Task Agent',
        'security': 'Security',
        'ar': 'Reports',
        'xm': 'XM Monitor',
        'aichat': 'Chat'
      };

      const module = moduleMap[pathParts[0]] || pathParts[0].toUpperCase();
      
      
      breadcrumbItems.push({
        label: module,
        path: null,
        isClickable: false
      });

      // Add remaining path parts
      pathParts.slice(1).forEach((part, index) => {
        const isLast = index === pathParts.length - 2;
        breadcrumbItems.push({
          label: part.split('-').map(p => p.charAt(0).toUpperCase() + p.slice(1)).join(' '),
          path: isLast ? currentPath : null,
          isClickable: false
        });
      });
    }
  }

  // Removed special handling for Transaction Management page - using standard route structure
  // Breadcrumb will be: Warehouse Management / Transactions / Transaction Management

  // Special handling for ViewDashboard page - add dashboard title from query parameter
  if (currentPath === '/db/ViewDashboard') {
    const urlParams = new URLSearchParams(window.location.search);
    const rowid = urlParams.get('rowid');
    
    if (rowid && breadcrumbItems.length > 0) {
      // Try to get dashboard title from DOM (DevExpress dashboard control might expose it)
      const dashboardTitleElement = document.querySelector('.dx-dashboard-title, .dx-dashboard-name, [data-dashboard-title], .dx-dashboard-toolbar-title');
      let dashboardTitle = null;
      
      if (dashboardTitleElement) {
        dashboardTitle = dashboardTitleElement.textContent?.trim() || dashboardTitleElement.getAttribute('data-dashboard-title') || dashboardTitleElement.innerText?.trim();
      }
      
      // If not found in DOM, try to get from localStorage or sessionStorage (if cached)
      if (!dashboardTitle) {
        try {
          const cachedDashboard = sessionStorage.getItem(`dashboard_${rowid}`);
          if (cachedDashboard) {
            const dashboardData = JSON.parse(cachedDashboard);
            dashboardTitle = dashboardData.name || dashboardData.title || dashboardData.dashboardName;
          }
        } catch (e) {
          // Ignore errors
        }
      }
      
      // If still not found, try to get from the dashboard instance if available
      if (!dashboardTitle) {
        try {
          // Check if DevExpress dashboard control is available and has title
          const dashboardElements = document.querySelectorAll('[class*="dx-dashboard"]');
          for (const elem of dashboardElements) {
            if (elem.__dashboardInstance) {
              const dashboard = elem.__dashboardInstance;
              if (dashboard.dashboardTitle) {
                dashboardTitle = dashboard.dashboardTitle;
                break;
              }
            }
          }
        } catch (e) {
          // Ignore errors
        }
      }
      
      // If title found, add it as the last breadcrumb item
      if (dashboardTitle) {
        breadcrumbItems.push({
          label: dashboardTitle,
          path: currentPath,
          isClickable: false
        });
      }
    }
  }

  return breadcrumbItems;
}

/**
 * Builds breadcrumb from treeview structure (if treeview data is available)
 * @param {Array} treeviewData - Treeview data structure
 * @param {string} currentPath - Current route pathname
 * @returns {Array} - Array of breadcrumb items
 */
export function buildBreadcrumbFromTreeview(treeviewData, currentPath) {
  if (!treeviewData || !Array.isArray(treeviewData)) {
    // Fallback to route-based breadcrumb
    return buildBreadcrumbFromRoute(currentPath);
  }

  const result = findItemByPath(treeviewData, currentPath);
  
  if (result) {
    const breadcrumbItems = [];
    
    // Build breadcrumb from parent chain
    result.parentChain.forEach(parent => {
      if (parent.text) {
        breadcrumbItems.push({
          label: parent.text,
          path: parent.path || null,
          isClickable: !!parent.path
        });
      }
    });
    
    // Add current item
    if (result.item.text) {
      breadcrumbItems.push({
        label: result.item.text,
        path: result.item.path || currentPath,
        isClickable: false
      });
    }
    
    return breadcrumbItems;
  }
  
  // Fallback to route-based breadcrumb
  return buildBreadcrumbFromRoute(currentPath);
}

